import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { user, t } = useApp();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/profile" replace />;
  }

  const passwordsMatch = password === repeatPassword;
  const canSubmit = password.length >= 8 && passwordsMatch && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error(t("Password must be at least 8 characters"));
      return;
    }

    if (password !== repeatPassword) {
      toast.error(t("Passwords do not match"));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(t("Password has been reset successfully"));
      navigate("/auth");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-md mx-auto" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-playfair text-3xl font-bold text-[#EDEDED] mb-2">
          {t("Reset your password")}
        </h1>
        <p className="text-[#D4D4D8] mb-6 text-sm">
          {t("Enter your new password below")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">{t("New Password")}</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Min 6 characters" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" required autoFocus />
          </div>
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">{t("Repeat New Password")}</Label>
            <Input type="password" value={repeatPassword} onChange={e => setRepeatPassword(e.target.value)}
              placeholder="Repeat new password" className={`bg-[#121212] border-[#27272A] text-[#EDEDED] h-11 ${repeatPassword && !passwordsMatch ? "border-[#EF4444]" : ""}`} required />
            {repeatPassword && !passwordsMatch && (
              <p className="text-[#EF4444] text-[11px] mt-1">{t("Passwords do not match")}</p>
            )}
          </div>
          <Button type="submit" disabled={!canSubmit} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 disabled:opacity-50" data-testid="reset-password-btn">
            {loading ? t("Please wait...") : t("Reset Password")}
          </Button>
        </form>

        <p className="text-center text-[#D4D4D8] text-sm mt-6">
          <a href="/auth" className="text-[#D4B483] font-medium hover:underline">
            {t("Back to Sign In")}
          </a>
        </p>
      </motion.div>
    </div>
  );
}
